import { useEffect, useState } from 'react'
import styled from 'styled-components'

const elementHeight = 40
export const NumberInput = ({
  id,
  onChange,
  startCount,
  maxValue = 10000000000,
  minValue = 0,
  quickRaise = false,
  unit,
  colorMode = 'light',
  inputWidth = elementHeight
}: {
  id: string
  onChange: (id: string, e: number) => void
  startCount?: number
  minValue?: number
  maxValue?: number
  quickRaise?: boolean
  unit?: string
  colorMode?: 'light' | 'dark'
  inputWidth?: number
}) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (startCount === undefined) return
    setCount(startCount)
  }, [startCount])

  useEffect(() => {
    if (!count && count !== 0) return
    // onChange(id, count)
  }, [count, id])

  return (
    <NumberInputRoot
      open={count > 0}
      className='numin'
      colorMode={colorMode}
      inputWidth={inputWidth}
    >
      {quickRaise && (
        <QuickLowerButton
          onClick={() => {
            onChange(id, Math.max(count - 10, minValue))
            setCount(Math.max(count - 10, minValue))
          }}
          open={count > 0}
          colorMode={colorMode}
        >
          &minus;10
        </QuickLowerButton>
      )}
      {quickRaise && (
        <QuickRaiseButton
          onClick={() => {
            onChange(id, Math.min(count + 10, maxValue))
            setCount(Math.min(count + 10, maxValue))
          }}
          open={count > 0}
          colorMode={colorMode}
        >
          +10
        </QuickRaiseButton>
      )}
      <Container open={count > 0} colorMode={colorMode} inputWidth={inputWidth}>
        {count > 0 && (
          <Count
            className='count-input'
            open={count > 0}
            value={count}
            onChange={e => {
              const val = +e.target.value
              if (isNaN(val)) return
              onChange(id, Math.min(val, maxValue))
              setCount(Math.min(val, maxValue))
            }}
            colorMode={colorMode}
            width={inputWidth}
          />
        )}
        <LowerButton
          className='primary'
          onClick={() => {
            onChange(id, Math.max(count - 1, minValue))
            setCount(Math.max(count - 1, minValue))
          }}
          open={count > 0}
          colorMode={colorMode}
        >
          &minus;
        </LowerButton>
        <RaiseButton
          className='primary'
          onClick={() => {
            onChange(id, Math.min(count + 1, maxValue))
            setCount(Math.min(count + 1, maxValue))
          }}
          open={count > 0}
          colorMode={colorMode}
        >
          +
        </RaiseButton>
      </Container>
      {unit && <Unit>{unit}</Unit>}
    </NumberInputRoot>
  )
}

const NumberInputRoot = styled.div<{
  open: boolean
  colorMode: 'light' | 'dark'
  inputWidth: number
}>`
  position: relative;
  font-family: var(--font-sans-serif);
  height: ${elementHeight}px;
  width: 100%;
  display: inline-block;
  width: ${({ inputWidth }) => elementHeight * 2 + inputWidth}px;
`
const ButtonBase = styled.button<{
  open: boolean
  colorMode?: 'light' | 'dark'
}>`
  position: absolute;
  padding: 0;
  width: ${elementHeight}px;
  height: ${elementHeight}px;
  padding-bottom: 2px;
  border: 0;

  background-color: ${({ open, colorMode }) =>
    open
      ? colorMode === 'dark'
        ? 'var(--brandblue-main)'
        : 'var(--brandblue-main)'
      : colorMode === 'dark'
      ? 'var(--brandblue-main)'
      : 'var(--brandblue-bright-2)'};

  transition: all 0.15s ease-out;
  color: ${({ open, colorMode }) =>
    open
      ? colorMode === 'dark'
        ? 'var(--text-color)'
        : 'var(--brandblue-bright-5)'
      : colorMode === 'dark'
      ? 'var(--brandblue-bright-5)'
      : '#fff'};

  font-family: var(--font-sans-serif);
  font-size: 20px;
  font-weight: 300;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${elementHeight}px;
  &:hover {
    cursor: pointer;
    background-color: ${({ colorMode }) =>
      colorMode === 'dark'
        ? 'var(--brandblue-bright-2)'
        : 'var(--brandblue-main)'};
    color: ${({ colorMode }) =>
      colorMode === 'dark' ? 'var(--brandblue-main)' : '#fff'};
    font-weight: 900;
  }
`
const LowerButton = styled(ButtonBase)`
  left: 0;
`
const RaiseButton = styled(ButtonBase)`
  right: 0;
`
const QuickRaiseBase = styled(ButtonBase)`
  font-weight: 400;
  font-size: 15px;
  width: ${elementHeight * 2}px;
  height: ${elementHeight}px;

  background-color: ${({ colorMode }) =>
    colorMode === 'dark'
      ? 'var(--brandblue-bright-1)'
      : 'var(--brandblue-bright-3)'};
  color: ${({ colorMode }) =>
    colorMode === 'dark' ? 'var(--text-color)' : 'var(--site-background)'};

  &:hover {
    background-color: ${({ colorMode }) =>
      colorMode === 'dark'
        ? 'var(--brandblue-bright-2)'
        : 'var(--brandblue-bright-5)'};
    color: ${({ colorMode }) =>
      colorMode === 'dark' ? 'var(--text-color)' : 'var(--brandblue-main)'};
    opacity: 0.8;
  }
`
const QuickRaiseButton = styled(QuickRaiseBase)`
  right: ${-elementHeight * 1.25}px;
  padding-left: 20px;
  border-top-right-radius: ${elementHeight}px;
  border-bottom-right-radius: ${elementHeight}px;
`
const QuickLowerButton = styled(QuickRaiseBase)`
  left: ${-elementHeight * 1.25}px;
  padding-right: 20px;
  border-top-left-radius: ${elementHeight}px;
  border-bottom-left-radius: ${elementHeight}px;
`
const Count = styled.input<{
  open: boolean
  colorMode: 'light' | 'dark'
  width?: number
}>`
  position: absolute;
  background-color: transparent;
  border: 0;
  left: 50%;
  text-align: center;
  transform: translateX(-50%);
  height: ${elementHeight}px;
  line-height: 35px;
  font-size: 22px;

  font-weight: 700;
  display: flex;
  align-items: center;
  color: ${({ open, colorMode }) =>
    open
      ? colorMode === 'dark'
        ? 'var(--text-color)'
        : 'var(--brandblue-bright-5)'
      : colorMode === 'dark'
      ? 'var(--brandblue-main)'
      : 'var(--brandblue-main)'};
  display: flex;
  align-items: center;
  width: ${({ width }) => (width ? `${width}px` : `${elementHeight}px`)};

  &:focus {
    outline: none;
  }
 
`

const Unit = styled.div`
  position: absolute;
  left: 50%;
  bottom: -26px;
  text-align: center;
  transform: translateX(-50%);
  font-size: 15px;
`
const Container = styled.div<{
  open: boolean
  colorMode: 'light' | 'dark'
  inputWidth: number
}>`
  position: absolute;
  right: 0;
  height: ${elementHeight}px;
  max-width: ${({ open, inputWidth }) =>
    open ? elementHeight * 2 + inputWidth + 'px' : elementHeight + 'px'};
  transition: all 0.25s ease-out;
  width: 100%;
  background-color: ${({ open, colorMode }) =>
    open
      ? colorMode === 'dark'
        ? 'var(--brandblue-main)'
        : 'var(--brandblue-main)'
      : colorMode === 'dark'
      ? 'var(--brandblue-main)'
      : 'var(--brandblue-bright-5)'};
 color: ${({ open, colorMode }) =>
   open
     ? colorMode === 'dark'
       ? 'var(--text-color)'
       : 'var(--brandblue-main)'
     : colorMode === 'dark'
     ? 'var(--text-color)'
     : 'var(--brandblue-bright-5)'};
  border-radius: ${elementHeight}px;
  overflow: hidden;
  &:hover {
    cursor: pointer;
    background-color: ${({ colorMode }) =>
      colorMode === 'dark' ? 'var(--brandblue-main)' : 'var(--brandblue-main)'}
  }
`
